
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Sun, Moon, Sunrise, Coffee, Droplets, PersonStanding
} from 'lucide-react';
import { useFasting } from '@/contexts/FastingContext';

interface Tip {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

export const TimeTip: React.FC = () => {
  const [currentTip, setCurrentTip] = useState<Tip | null>(null);
  const { isActive } = useFasting();
  
  useEffect(() => {
    const getCurrentTip = () => {
      const now = new Date();
      const hour = now.getHours();
      
      // Get sunrise and sunset times (approximate)
      const isSummer = now.getMonth() >= 3 && now.getMonth() <= 8;
      const sunriseHour = isSummer ? 5 : 7; // Earlier sunrise in summer
      const sunsetHour = isSummer ? 20 : 17; // Later sunset in summer
      
      let tip: Tip;
      
      // Early morning (5-8)
      if (hour >= sunriseHour && hour < sunriseHour + 3) {
        tip = {
          icon: <Sunrise size={24} />,
          title: "Morning Ritual",
          description: isActive 
            ? "Start your day with a glass of warm water with lemon. It helps detoxify and has minimal impact on your fast."
            : "Try having a light protein-rich breakfast to kickstart your metabolism.",
          color: "text-orange-500"
        };
      }
      // Morning (8-11)
      else if (hour >= 8 && hour < 11) {
        tip = {
          icon: <Coffee size={24} />,
          title: "Morning Focus",
          description: isActive 
            ? "Black coffee or green tea can help maintain focus during your fast and may enhance fat burning."
            : "Consider having your most substantial meal now when your metabolism is active.",
          color: "text-purple-500"
        };
      }
      // Midday (11-14)
      else if (hour >= 11 && hour < 14) {
        tip = {
          icon: <Sun size={24} />,
          title: "Midday Movement",
          description: isActive 
            ? "Take a short 10-minute walk to boost energy and aid fat burning during your fast."
            : "Eating a balanced lunch now helps prevent afternoon energy crashes.",
          color: "text-yellow-500"
        };
      }
      // Afternoon (14-17)
      else if (hour >= 14 && hour < 17) {
        tip = {
          icon: <Droplets size={24} />,
          title: "Hydration Check",
          description: "Remember to stay hydrated. If feeling hungry while fasting, try sparkling water with a splash of apple cider vinegar.",
          color: "text-blue-500"
        };
      }
      // Pre-sunset (sunset - 1hr to sunset)
      else if (hour >= sunsetHour - 1 && hour < sunsetHour) {
        tip = {
          icon: <PersonStanding size={24} />,
          title: "Evening Walk",
          description: "Take advantage of the golden hour with a walk before sunset. This gentle activity can reduce stress and improve sleep quality.",
          color: "text-orange-400"
        };
      }
      // Evening (sunset to sunset + 3)
      else if (hour >= sunsetHour && hour < sunsetHour + 3) {
        tip = {
          icon: <Moon size={24} />,
          title: "Evening Routine",
          description: isActive 
            ? "If fasting into the evening, consider light stretching to help relax the body."
            : "Try to have your last meal of the day now, at least 3 hours before bedtime.",
          color: "text-indigo-500"
        };
      }
      // Night (After 22 or sunset + 3)
      else {
        tip = {
          icon: <Moon size={24} />,
          title: "Wind Down",
          description: "Prepare for quality sleep. Avoid blue light and consider herbal tea like chamomile if not fasting.",
          color: "text-blue-700"
        };
      }
      
      setCurrentTip(tip);
    };
    
    getCurrentTip();
    
    // Update tip every hour
    const interval = setInterval(getCurrentTip, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isActive]);
  
  if (!currentTip) return null;
  
  return (
    <Card className="w-full border-fasttrack-light-blue/30 mt-4 bg-gradient-to-r from-fasttrack-light-blue/10 to-fasttrack-blue/5">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`mt-1 ${currentTip.color}`}>
            {currentTip.icon}
          </div>
          <div>
            <h3 className={`font-medium mb-1 ${currentTip.color}`}>{currentTip.title}</h3>
            <p className="text-sm text-muted-foreground">{currentTip.description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
